export type VerificationSelection = Record<'suite' | 'package' | 'profile', {
  selected_revision_id: string | null;
  selection_generation: number;
}>;

export type VerificationPreparation = {
  expected_selection: VerificationSelection;
  purpose: 'diagnostic' | 'acceptance';
  comparison_base_version_id?: string;
};

export type VerificationPreparationResult =
  | { status: 'prepared' | 'not_prepared' }
  | { status: 'selection_stale'; message: string };

export type VerificationAction = {
  id: string;
  kind: 'local_boundary' | 'native_case' | 'diagnostic' | 'context';
  check_id: string | null;
  reason_code: string;
  explanation: string;
  proof_scope: string;
  proof_surface: string;
  eligibility: string;
  coverage_basis?: 'declaration' | 'established' | 'heuristic' | 'unknown';
  target_step_ids?: string[];
  requirement_refs?: string[];
  limitations: string[];
  next_operation: string | null;
  evidence?: { reference_id: string; owner: string; status: string }[];
  footprint?: { scheduled_step_ids: string[]; fresh_step_ids: string[]; asserted_step_ids: string[] };
  cost: {
    status: string;
    sample_count: number;
    observed_wall_seconds: number[];
    observed_cumulative_execution_seconds?: number[];
    note: string;
  };
};

export const verificationGroups = [
  ['run_now', 'Run now'],
  ['reuse', 'Reuse evidence'],
  ['reevaluate', 'Reevaluate'],
  ['acceptance_remaining', 'Acceptance remaining'],
  ['blocked_or_unknown', 'Blocked or unknown'],
  ['not_selected', 'Not selected'],
] as const;

export type VerificationPlan = Record<typeof verificationGroups[number][0], VerificationAction[]> & {
  status: string;
  digest: string;
  selection: VerificationSelection;
  complete: boolean;
  continuation: string | null;
  unresolved_check_ids: string[];
  warnings: string[];
  changed_step_ids?: string[];
  change_categories?: string[];
  profile_status?: 'missing' | 'current' | 'stale' | null;
  member_profile_statuses?: Record<string, 'missing' | 'current' | 'stale'>;
};

export type VerificationProfileContext = {
  status: 'missing' | 'current' | 'stale';
  selection: VerificationSelection;
  profile: { revision_id: string; snapshot: Record<string, unknown> } | null;
};

export type VerificationInventory = {
  checks: {
    id: string;
    kind: string;
    required: boolean;
    exact_intent_duplicates?: string[];
    evidence: VerificationAction;
  }[];
  total: number;
  complete: boolean;
  next_cursor: string | null;
  evidence_complete: boolean;
  note: string;
};

export type VerificationChangeRequest = {
  operation: 'promote_diagnostic' | 'retire_case' | 'retire_fixture';
  automation_version_id: string;
  expected_selection: VerificationSelection;
  reason: string;
  request_id: string;
  case_id?: string;
  source_run_id?: string;
  fixture_id?: string;
  preview_digest?: string;
};

export type VerificationChangeReceipt = {
  request: VerificationChangeRequest;
  preview: { allowed: boolean; reason: string; preview_digest: string; [key: string]: unknown };
  state: 'reviewed' | 'pending' | 'committed';
};
